<template>
	<BaseLayout :footer-full="true" :include-padding="true" background-color-class="bg-color-white">
		<BasePlaceHolder :class="['my-100', { container: loading }]" :line-count="8" :placehold="loading">
			<div class="container">
				<AssetDetailsHero v-if="asset" :asset="asset" :is-company-asset="isCompanyAsset" />
			</div>

			<JumpLinkNav :jump-links="jumpLinks" :root-margin="200" class="mt-200" data-test="asset-details-jump-links">
				<div
					v-if="asset && showAboutSection"
					id="about-company-asset"
					class="container mt-150 mt-400-md"
					data-context="about-company-asset"
				>
					<AssetDetailsAboutSection :asset="asset" />
				</div>
				<div
					v-if="asset && showInvestmentOverviewSection"
					id="investment-overview"
					class="container mt-150 mt-400-md"
					data-context="investment-overview"
				>
					<AssetDetailsInvestmentOverview :asset="asset" />
				</div>

				<div
					v-if="asset && showUpdatesSection"
					id="updates"
					class="container mt-150 mt-400-md"
					data-context="updates"
				>
					<h2 class="heading-4 mb-100">{{ updatesHeading }}</h2>
					<HorizontalMediaCard
						v-for="update in updates"
						:key="update.title"
						class="col-9-md"
						:content="update"
					/>
				</div>

				<div
					v-if="asset && showFundsSection"
					id="fund-information"
					class="container mt-150 mt-400-md pb-150-smo"
					data-context="fund-information"
				>
					<AssetDetailsFunds :asset="asset" />
				</div>
			</JumpLinkNav>
		</BasePlaceHolder>
		<template #footer>
			<BasePlaceHolder :line-count="3" :placehold="loading" :class="{ container: loading }">
				<FooterFull :disclaimers="disclaimers" />
			</BasePlaceHolder>
		</template>
	</BaseLayout>
</template>

<script setup lang="ts">
import { Asset, AssetFundAllocation, AssetUpdateSanity } from 'types/assets';
import { computed, Ref, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AssetDetailsAboutSection from '@components/marketing-site/asset-details/asset-details-about-section.vue';
import AssetDetailsFunds from '@components/marketing-site/asset-details/asset-details-funds.vue';
import AssetDetailsHero from '@components/marketing-site/asset-details/asset-details-hero.vue';
import AssetDetailsInvestmentOverview from '@components/marketing-site/asset-details/asset-details-investment-overview.vue';
import { AssetQueryParams } from 'types/sanity/proxy';
import FooterFull from '@components/footer/footer-full.vue';
import { getAsset } from '@api/sanity/proxy/assets';
import HorizontalMediaCard from '@components/marketing-site/horizontal-media-card.vue';
import { HorizontalMediaCardContent } from 'types/marketing-site';
import { JumpLink } from 'types/layout';
import JumpLinkNav from '@components/layout/jump-link-nav.vue';
import { MixpanelContext } from 'types/analytics';
import { pluralize } from '@filters/shared-filters';
import { useAnalyticsProperties } from '@utils/composables/use-analytics-properties';
import { useHead } from '@unhead/vue';
import { useMixpanel } from '@utils/composables/use-mixpanel';

const route = useRoute();
const router = useRouter();
const { setViewProperties, setCustomProperties } = useAnalyticsProperties();

const asset: Ref<Asset | null> = ref(null);
const { trackMixpanelView } = useMixpanel();

const loading = ref(true);

const isCompanyAsset = computed((): boolean => 'companyId' in route.params);

const funds = computed((): Array<AssetFundAllocation> => {
	return asset.value?.fundAllocations ?? [];
});

const showAboutSection = computed((): boolean => !!asset.value?.ventureCapitalFields?.aboutAssetSectionContent?.length);

const showInvestmentOverviewSection = computed((): boolean => !!asset.value?.ventureCapitalFields);

const showUpdatesSection = computed((): boolean => {
	return !!asset.value?.associatedUpdates?.length;
});

const showFundsSection = computed((): boolean => !!funds.value.length);

const updatesHeading = computed((): string => pluralize(asset.value?.associatedUpdates?.length ?? 0, 'Update'));

const jumpLinks = computed((): Array<JumpLink> => {
	const jumpLinksToReturn = [];

	if (asset.value && showAboutSection.value) {
		jumpLinksToReturn.push({
			key: 'about-company-asset',
			label: `About ${asset.value?.name}`
		});
	}

	if (showInvestmentOverviewSection.value) {
		jumpLinksToReturn.push({
			key: 'investment-overview',
			label: 'Investment overview'
		});
	}

	if (asset.value && showUpdatesSection.value) {
		jumpLinksToReturn.push({
			key: 'updates',
			label: 'Updates'
		});
	}

	if (asset.value && showFundsSection.value)
		jumpLinksToReturn.push({
			key: 'fund-information',
			label: 'Fund information'
		});

	return jumpLinksToReturn;
});

const viewName = ref('Asset Detail');
const viewTitle = computed(() => asset.value?.name ?? asset.value?.assetType.name);
const customProperties = computed(
	(): MixpanelContext => ({
		'Asset Class': funds.value[0]?.fund?.assetClass.enumValue ?? ''
	})
);

const disclaimers = computed(() => {
	return asset.value?.footerDisclaimer ?? [];
});

const updates = computed(() => asset.value?.associatedUpdates?.map((update) => formatMediaCardContent(update)));

setViewProperties({ viewName, viewTitle });
setCustomProperties(customProperties);

function formatMediaCardContent(update: AssetUpdateSanity): HorizontalMediaCardContent {
	return {
		thumbnailUrl: update.thumbnailUrl,
		title: update.title,
		description: update.subtitle,
		date: update.displayDate,
		link: {
			router: 'investor-update',
			params: {
				investorUpdateId: update.fundriseId
			}
		}
	};
}

async function setup(): Promise<void> {
	try {
		if (isCompanyAsset.value) {
			const assetQueryParams: AssetQueryParams = {
				elementType: 'VENTURE',
				include: ['INVESTOR_UPDATES']
			};
			asset.value = await getAsset(route.params.companyId as string, assetQueryParams);
		} else {
			const assetQueryParams: AssetQueryParams = {
				elementType: 'VENTURE_FUND',
				include: ['INVESTOR_UPDATES']
			};
			asset.value = await getAsset(route.params.ventureFundId as string, assetQueryParams);
		}

		if (!asset.value) {
			await router.push({ name: '404' });
		}

		trackMixpanelView('Page', {
			'View Name': viewName.value,
			'View Title': viewTitle.value,
			customProperties: customProperties.value
		});

		const metaTitle = asset.value?.name;

		useHead({
			title: metaTitle,
			meta: [{ property: 'og:title', content: metaTitle }]
		});

		loading.value = false;
	} catch (e) {
		await router.push({ name: '404' });
	}
}

setup();
</script>
